import React, { useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import isEqual from 'lodash/isEqual';
import {
  useAdditionalTradingFormContext,
  useTradingFormContext
} from '../trading-form-context';
import { FormContainer } from '@client/foundation/layout/form-container';
import { TradingFormField } from '../trading-form-field';
import { StrategyPath } from './strategy-config-form.utils';
import { useFieldArray, useFormContext } from 'react-hook-form';
import AddIcon from '@mui/icons-material/Add';
import { FormArrayItem } from '@client/foundation/form/form-array-item';
import { Section } from '@client/foundation/layout/section';
import {
  LiquidationSignalMode,
  MaRelativeMode,
  MaSlopeNormalization,
  MaTrendDirection,
  RelativePosition,
  RsiMode,
  SignalType,
  ValueMode,
  ValueReactionDirection,
  ValueReactionEvent,
  ValueReactionScope
} from '@shared/types';
import {
  LiquidationSignalParams,
  MaRelativeSignalParams,
  RsiSignalParams,
  SignalCondition,
  ValueRelativeSignalParams,
  ValueSignalParams,
  ValueReactionSignalParams
} from '@server/data-feed/schemas/input.schema';
import { SignalBlockTarget } from '@server/trading-templates/schemas/input.schema';
import { useTemplates } from '@client/features/trading-templates/create-template/templates.hook';
import { UiStatePath } from '../trading-form-ui-state.hook';
import {
  sectionHeaderPanelStyle,
  sectionPanelStyle
} from '@client/foundation/layout/styling';
import { AppIconButton } from '@client/foundation/design-system/components';
import { FormConfigButtons } from '../form-config-buttons';
import { colorTokens } from '@client/foundation/design-system/tokens';
import {
  defaultLiquidationParams,
  defaultMaReversalParams,
  defaultMaToMaParams,
  defaultMaRelativeParams,
  defaultRsiParams,
  defaultValueReactionParams,
  defaultValueRelativeParams,
  strategySignalOptions
} from '@client/widgets/chart/constants/signal-defaults';
const emptySignalConditions: SignalCondition[] = [];

type StrategyBucket = 'entries' | 'exits';

// Autocomplete can emit values outside SignalType (e.g. null/string[]), so we guard before update().
const isSignalType = (value: unknown): value is SignalType => {
  return (
    typeof value === 'string' &&
    strategySignalOptions.includes(value as SignalType)
  );
};

const isValueMode = (value: unknown): value is ValueMode => {
  return (
    typeof value === 'string' &&
    Object.values(ValueMode).includes(value as ValueMode)
  );
};

// Preserve relative direction when editing an existing row; fallback to default direction.
const getRelativePosition = (
  prev: SignalCondition | undefined
): { position: keyof typeof RelativePosition } => {
  if (
    prev?.value === SignalType.VALUE &&
    prev.params.mode === ValueMode.REACTION
  ) {
    return {
      position:
        prev.params.direction === ValueReactionDirection.BULLISH
          ? RelativePosition.ABOVE
          : RelativePosition.BELOW
    };
  }

  if (prev && 'params' in prev && 'position' in prev.params) {
    return { position: prev.params.position };
  }

  return { position: RelativePosition.BELOW };
};

const getValueReactionDirection = (
  prev: SignalCondition | undefined
): { direction: keyof typeof ValueReactionDirection } => {
  if (
    prev?.value === SignalType.VALUE &&
    prev.params.mode === ValueMode.REACTION
  ) {
    return { direction: prev.params.direction };
  }

  if (prev && 'params' in prev && 'position' in prev.params) {
    return {
      direction:
        prev.params.position === RelativePosition.ABOVE
          ? ValueReactionDirection.BULLISH
          : ValueReactionDirection.BEARISH
    };
  }

  return { direction: ValueReactionDirection.BEARISH };
};

const getValueReactionEvent = (
  prev: SignalCondition | undefined
): { event: keyof typeof ValueReactionEvent } => {
  if (
    prev?.value === SignalType.VALUE &&
    prev.params.mode === ValueMode.REACTION
  ) {
    return { event: prev.params.event };
  }

  return { event: ValueReactionEvent.RETEST };
};

const getValueReactionParams = (
  prev: SignalCondition | undefined
): ValueReactionSignalParams => {
  if (
    prev?.value === SignalType.VALUE &&
    prev.params.mode === ValueMode.REACTION
  ) {
    return {
      ...defaultValueReactionParams,
      ...prev.params
    };
  }

  return {
    ...defaultValueReactionParams,
    ...getValueReactionDirection(prev),
    ...getValueReactionEvent(prev)
  };
};

const getValueRelativeParams = (
  prev: SignalCondition | undefined
): ValueRelativeSignalParams => {
  if (
    prev?.value === SignalType.VALUE &&
    prev.params.mode === ValueMode.RELATIVE
  ) {
    return {
      ...defaultValueRelativeParams,
      ...prev.params
    };
  }

  return {
    ...defaultValueRelativeParams,
    ...getRelativePosition(prev)
  };
};

const getValueParams = (
  prev: SignalCondition | undefined
): ValueSignalParams => {
  if (
    prev?.value === SignalType.VALUE &&
    prev.params.mode === ValueMode.REACTION
  ) {
    return getValueReactionParams(prev);
  }

  return getValueRelativeParams(prev);
};

const getMaRelativeParams = (
  prev: SignalCondition | undefined
): MaRelativeSignalParams => {
  const maPeriodFromPrev =
    prev?.value === SignalType.MA
      ? prev.params.period
      : prev && 'params' in prev && 'period' in prev.params
        ? prev.params.period
        : undefined;
  const maPeriod =
    typeof maPeriodFromPrev === 'number' &&
    Number.isInteger(maPeriodFromPrev) &&
    maPeriodFromPrev > 0
      ? maPeriodFromPrev
      : defaultMaRelativeParams.period;

  if (prev?.value === SignalType.MA) {
    if (prev.params.mode === MaRelativeMode.PRICE_RELATIVE) {
      return {
        mode: MaRelativeMode.PRICE_RELATIVE,
        period: maPeriod,
        position: prev.params.position
      };
    }

    if (prev.params.mode === MaRelativeMode.PRICE_DEVIATION) {
      return {
        mode: MaRelativeMode.PRICE_DEVIATION,
        period: maPeriod,
        position: prev.params.position,
        minDistancePct: prev.params.minDistancePct
      };
    }

    if (prev.params.mode === MaRelativeMode.MA_TO_MA) {
      return {
        mode: MaRelativeMode.MA_TO_MA,
        period: maPeriod,
        referencePeriod:
          typeof prev.params.referencePeriod === 'number' &&
          Number.isInteger(prev.params.referencePeriod) &&
          prev.params.referencePeriod > 0
            ? prev.params.referencePeriod
            : defaultMaToMaParams.referencePeriod,
        position:
          prev.params.position === RelativePosition.ABOVE
            ? RelativePosition.ABOVE
            : RelativePosition.BELOW
      };
    }

    if (prev.params.mode === MaRelativeMode.SLOPE) {
      return {
        mode: MaRelativeMode.SLOPE,
        period: maPeriod,
        direction: prev.params.direction,
        lookbackBars: prev.params.lookbackBars,
        minAngleDeg: prev.params.minAngleDeg,
        normalization: prev.params.normalization,
        atrPeriod: prev.params.atrPeriod
      };
    }

    if (prev.params.mode === MaRelativeMode.REVERSAL) {
      const legacyMinAngle =
        typeof (prev.params as { minAngleDeg?: unknown }).minAngleDeg ===
        'number'
          ? ((prev.params as { minAngleDeg?: number }).minAngleDeg ?? 5)
          : 5;

      return {
        mode: MaRelativeMode.REVERSAL,
        period: maPeriod,
        direction:
          prev.params.direction === MaTrendDirection.DOWN
            ? MaTrendDirection.DOWN
            : MaTrendDirection.UP,
        minAngleUpDeg:
          typeof prev.params.minAngleUpDeg === 'number'
            ? prev.params.minAngleUpDeg
            : legacyMinAngle,
        minAngleDownDeg:
          typeof prev.params.minAngleDownDeg === 'number'
            ? prev.params.minAngleDownDeg
            : legacyMinAngle,
        normalization:
          prev.params.normalization === MaSlopeNormalization.ATR
            ? MaSlopeNormalization.ATR
            : MaSlopeNormalization.PCT,
        atrPeriod:
          typeof prev.params.atrPeriod === 'number' &&
          Number.isInteger(prev.params.atrPeriod) &&
          prev.params.atrPeriod > 0
            ? prev.params.atrPeriod
            : defaultMaReversalParams.atrPeriod,
        keepSignalState:
          typeof prev.params.keepSignalState === 'boolean'
            ? prev.params.keepSignalState
            : defaultMaReversalParams.keepSignalState
      };
    }

    return {
      mode: MaRelativeMode.TREND,
      period: maPeriod,
      direction: prev.params.direction,
      lookbackBars: prev.params.lookbackBars
    };
  }

  if (prev && 'params' in prev && 'position' in prev.params) {
    return {
      mode: MaRelativeMode.PRICE_RELATIVE,
      period: maPeriod,
      position: prev.params.position
    };
  }

  return defaultMaRelativeParams;
};

const getRsiParams = (prev: SignalCondition | undefined): RsiSignalParams => {
  if (prev?.value === SignalType.RSI) {
    if (prev.params.mode === RsiMode.AVERAGE_RELATIVE) {
      return {
        mode: RsiMode.AVERAGE_RELATIVE,
        position: prev.params.position,
        lookbackBars: prev.params.lookbackBars ?? 3,
        minMaDelta: prev.params.minMaDelta ?? 0
      };
    }

    const levelParams =
      prev.params.mode === RsiMode.LEVEL ? prev.params : defaultRsiParams;

    return {
      mode: RsiMode.LEVEL,
      position: levelParams.position ?? defaultRsiParams.position,
      level: levelParams.level ?? defaultRsiParams.level,
      confirmationCandles:
        levelParams.confirmationCandles ?? defaultRsiParams.confirmationCandles
    };
  }

  if (prev && 'params' in prev && 'position' in prev.params) {
    return {
      ...defaultRsiParams,
      position: prev.params.position
    };
  }

  return defaultRsiParams;
};

const getLiquidationParams = (
  prev: SignalCondition | undefined
): LiquidationSignalParams => {
  if (prev?.value === SignalType.LIQUIDATION) {
    return {
      ...defaultLiquidationParams,
      ...prev.params
    };
  }

  return defaultLiquidationParams;
};

// We rebuild the whole row on signal type change because SignalCondition is a discriminated union.
// Changing "value" changes the required data shape; keeping old params would leave invalid payload.
// In current setup each signal variant owns its typed params, so we set defaults here.
const buildUpdatedSignalCondition = (
  prev: SignalCondition | undefined,
  value: SignalType
): SignalCondition => {
  const operator = prev?.operator ?? 'AND';
  const plot = prev?.plot;
  const enabled = prev?.enabled !== false;

  if (value === SignalType.VALUE) {
    return {
      operator,
      value,
      enabled,
      plot,
      params: getValueParams(prev)
    };
  }

  if (value === SignalType.MA) {
    return {
      operator,
      value,
      enabled,
      plot,
      params: getMaRelativeParams(prev)
    };
  }

  if (value === SignalType.RSI) {
    return {
      operator,
      value,
      enabled,
      plot,
      params: getRsiParams(prev)
    };
  }

  if (value === SignalType.LIQUIDATION) {
    return {
      operator,
      value,
      enabled,
      plot,
      params: getLiquidationParams(prev)
    };
  }

  return {
    operator,
    value,
    enabled,
    plot,
    params: getValueRelativeParams(prev)
  };
};

const buildUpdatedValueSignalCondition = (
  prev: SignalCondition | undefined,
  mode: ValueMode
): SignalCondition => ({
  operator: prev?.operator ?? 'AND',
  value: SignalType.VALUE,
  enabled: prev?.enabled !== false,
  plot: prev?.plot,
  // VALUE uses mode-specific params shape; switching mode must rebuild params
  // to avoid leaving stale/partial fields from the previous mode.
  params:
    mode === ValueMode.REACTION
      ? getValueReactionParams(prev)
      : getValueRelativeParams(prev)
});

const normalizeValueSignalCondition = (
  condition: SignalCondition
): SignalCondition => {
  if (condition.value !== SignalType.VALUE) {
    return condition;
  }

  // Stored rows can be partial (especially after schema evolution), so we
  // hydrate missing mode-specific fields from defaults before rendering.
  return {
    ...condition,
    params:
      condition.params.mode === ValueMode.REACTION
        ? { ...defaultValueReactionParams, ...condition.params }
        : { ...defaultValueRelativeParams, ...condition.params }
  };
};

const normalizeMaSignalCondition = (
  condition: SignalCondition
): SignalCondition => {
  if (condition.value !== SignalType.MA) {
    return condition;
  }

  return {
    ...condition,
    params: getMaRelativeParams(condition)
  };
};

const normalizeLiquidationSignalCondition = (
  condition: SignalCondition
): SignalCondition => {
  if (condition.value !== SignalType.LIQUIDATION) {
    return condition;
  }

  return {
    ...condition,
    params: getLiquidationParams(condition)
  };
};

const normalizeSignalCondition = (
  condition: SignalCondition
): SignalCondition => {
  if (condition.value === SignalType.VALUE) {
    return normalizeValueSignalCondition(condition);
  }

  if (condition.value === SignalType.MA) {
    return normalizeMaSignalCondition(condition);
  }

  if (condition.value === SignalType.LIQUIDATION) {
    return normalizeLiquidationSignalCondition(condition);
  }

  return condition;
};

// Derive the exact type expected by TradingFormField.name
type TradingFormFieldName = NonNullable<
  React.ComponentProps<typeof TradingFormField>['name']
>;

// Helper to shape dynamic names and cast just once
const makeFieldName = (
  base: string,
  index: number,
  prop:
    | 'enabled'
    | 'operator'
    | 'value'
    | 'plot'
    | 'params.scope'
    | 'params.position'
    | 'params.period'
    | 'params.referencePeriod'
    | 'params.lookbackBars'
    | 'params.minMaDelta'
    | 'params.mode'
    | 'params.level'
    | 'params.direction'
    | 'params.minDistancePct'
    | 'params.minAngleDeg'
    | 'params.minAngleUpDeg'
    | 'params.minAngleDownDeg'
    | 'params.normalization'
    | 'params.atrPeriod'
    | 'params.event'
    | 'params.touchTolerancePct'
    | 'params.breakMinDistancePct'
    | 'params.preBreakCandles'
    | 'params.keepSignalState'
    | 'params.confirmationCandles'
    | 'params.maxHistoricalLevels'
    | 'params.cooldownBarsPerLevel'
): TradingFormFieldName => `${base}.${index}.${prop}` as TradingFormFieldName;

// Render signal-specific parameter fields (same pattern as indicator-specific form fields).
const renderSignalParameterFields = (
  base: string,
  index: number,
  fieldValue: SignalCondition | undefined,
  onValueModeChange?: (mode: ValueMode) => void
) => {
  switch (fieldValue?.value) {
    case SignalType.RSI: {
      const rsiMode = fieldValue.params?.mode ?? defaultRsiParams.mode;

      return (
        <>
          <TradingFormField
            type="select"
            name={makeFieldName(base, index, 'params.mode')}
            label="Mode"
            options={Object.values(RsiMode)}
            defaultValue={defaultRsiParams.mode}
          />
          <TradingFormField
            type="select"
            name={makeFieldName(base, index, 'params.position')}
            label="Position"
            options={Object.values(RelativePosition)}
            defaultValue={defaultRsiParams.position}
          />
          {rsiMode === RsiMode.LEVEL && (
            <>
              <TradingFormField
                type="numeric"
                name={makeFieldName(base, index, 'params.level')}
                label="Level"
                inputProps={{ min: 0, max: 100, step: 0.1 }}
              />
              <TradingFormField
                type="numeric"
                name={makeFieldName(base, index, 'params.confirmationCandles')}
                label="Confirmation candles"
                inputProps={{ min: 1, step: 1 }}
              />
            </>
          )}
          {rsiMode === RsiMode.AVERAGE_RELATIVE && (
            <>
              <TradingFormField
                type="numeric"
                name={makeFieldName(base, index, 'params.lookbackBars')}
                label="Lookback bars"
                inputProps={{ min: 1, step: 1 }}
              />
              <TradingFormField
                type="numeric"
                name={makeFieldName(base, index, 'params.minMaDelta')}
                label="Min MA delta"
                inputProps={{ min: 0, step: 0.0001 }}
              />
            </>
          )}
        </>
      );
    }
    case SignalType.MA: {
      const mode = fieldValue.params?.mode ?? defaultMaRelativeParams.mode;

      return (
        <>
          <TradingFormField
            type="numeric"
            name={makeFieldName(base, index, 'params.period')}
            label="MA period"
            inputProps={{ min: 1, step: 1 }}
          />
          <TradingFormField
            type="select"
            name={makeFieldName(base, index, 'params.mode')}
            label="Mode"
            options={Object.values(MaRelativeMode)}
            defaultValue={defaultMaRelativeParams.mode}
          />
          {mode === MaRelativeMode.TREND && (
            <>
              <TradingFormField
                type="select"
                name={makeFieldName(base, index, 'params.direction')}
                label="Direction"
                options={Object.values(MaTrendDirection)}
                defaultValue={defaultMaRelativeParams.direction}
              />
              <TradingFormField
                type="numeric"
                name={makeFieldName(base, index, 'params.lookbackBars')}
                label="Lookback bars"
                inputProps={{ min: 1, step: 1 }}
              />
            </>
          )}
          {mode === MaRelativeMode.PRICE_RELATIVE && (
            <TradingFormField
              type="select"
              name={makeFieldName(base, index, 'params.position')}
              label="Position"
              options={Object.values(RelativePosition)}
              defaultValue={RelativePosition.ABOVE}
            />
          )}
          {mode === MaRelativeMode.PRICE_DEVIATION && (
            <>
              <TradingFormField
                type="select"
                name={makeFieldName(base, index, 'params.position')}
                label="Position"
                options={Object.values(RelativePosition)}
                defaultValue={RelativePosition.ABOVE}
              />
              <TradingFormField
                type="numeric"
                name={makeFieldName(base, index, 'params.minDistancePct')}
                label="Min distance %"
                inputProps={{ min: 0, max: 100, step: 0.01 }}
              />
            </>
          )}
          {mode === MaRelativeMode.MA_TO_MA && (
            <>
              <TradingFormField
                type="numeric"
                name={makeFieldName(base, index, 'params.referencePeriod')}
                label="Reference MA period"
                inputProps={{ min: 1, step: 1 }}
              />
              <TradingFormField
                type="select"
                name={makeFieldName(base, index, 'params.position')}
                label="Position"
                options={Object.values(RelativePosition)}
                defaultValue={defaultMaToMaParams.position}
              />
            </>
          )}
          {mode === MaRelativeMode.SLOPE &&
            (() => {
              const normalization =
                fieldValue.params?.mode === MaRelativeMode.SLOPE
                  ? fieldValue.params.normalization
                  : MaSlopeNormalization.PCT;

              return (
                <>
                  <TradingFormField
                    type="select"
                    name={makeFieldName(base, index, 'params.direction')}
                    label="Direction"
                    options={Object.values(MaTrendDirection)}
                    defaultValue={MaTrendDirection.UP}
                  />
                  <TradingFormField
                    type="select"
                    name={makeFieldName(base, index, 'params.normalization')}
                    label="Normalization"
                    options={Object.values(MaSlopeNormalization)}
                    defaultValue={MaSlopeNormalization.PCT}
                  />
                  {normalization === MaSlopeNormalization.ATR && (
                    <TradingFormField
                      type="numeric"
                      name={makeFieldName(base, index, 'params.atrPeriod')}
                      label="ATR period"
                      inputProps={{ min: 1, step: 1 }}
                    />
                  )}
                  <TradingFormField
                    type="numeric"
                    name={makeFieldName(base, index, 'params.minAngleDeg')}
                    label="Min angle (deg)"
                    inputProps={{ min: 0, max: 89, step: 0.1 }}
                  />
                </>
              );
            })()}
          {mode === MaRelativeMode.REVERSAL &&
            (() => {
              const normalization =
                fieldValue.params?.mode === MaRelativeMode.REVERSAL
                  ? fieldValue.params.normalization
                  : defaultMaReversalParams.normalization;

              return (
                <>
                  <TradingFormField
                    type="select"
                    name={makeFieldName(base, index, 'params.direction')}
                    label="Direction"
                    options={Object.values(MaTrendDirection)}
                    defaultValue={defaultMaReversalParams.direction}
                  />
                  <TradingFormField
                    type="select"
                    name={makeFieldName(base, index, 'params.normalization')}
                    label="Normalization"
                    options={Object.values(MaSlopeNormalization)}
                    defaultValue={defaultMaReversalParams.normalization}
                  />
                  {normalization === MaSlopeNormalization.ATR && (
                    <TradingFormField
                      type="numeric"
                      name={makeFieldName(base, index, 'params.atrPeriod')}
                      label="ATR period"
                      inputProps={{ min: 1, step: 1 }}
                    />
                  )}
                  <TradingFormField
                    type="numeric"
                    name={makeFieldName(base, index, 'params.minAngleUpDeg')}
                    label="Min angle UP (deg)"
                    inputProps={{ min: 0, max: 89, step: 0.1 }}
                  />
                  <TradingFormField
                    type="numeric"
                    name={makeFieldName(base, index, 'params.minAngleDownDeg')}
                    label="Min angle DOWN (deg)"
                    inputProps={{ min: 0, max: 89, step: 0.1 }}
                  />
                  <TradingFormField
                    type="checkbox"
                    name={makeFieldName(base, index, 'params.keepSignalState')}
                    label="Keep signal state"
                    checked={
                      fieldValue.params?.mode === MaRelativeMode.REVERSAL
                        ? Boolean(fieldValue.params.keepSignalState)
                        : defaultMaReversalParams.keepSignalState
                    }
                  />
                </>
              );
            })()}
        </>
      );
    }
    case SignalType.VALUE: {
      const mode = fieldValue.params?.mode ?? defaultValueRelativeParams.mode;
      const reactionParams =
        fieldValue.params?.mode === ValueMode.REACTION
          ? fieldValue.params
          : defaultValueReactionParams;
      const scope = reactionParams.scope ?? defaultValueReactionParams.scope;
      const event = reactionParams.event ?? defaultValueReactionParams.event;

      return (
        <>
          <TradingFormField
            type="select"
            name={makeFieldName(base, index, 'params.mode')}
            label="Mode"
            options={Object.values(ValueMode)}
            defaultValue={defaultValueRelativeParams.mode}
            onChange={(_, nextValue) => {
              if (!isValueMode(nextValue)) return;
              onValueModeChange?.(nextValue);
            }}
          />
          {mode === ValueMode.RELATIVE && (
            <TradingFormField
              type="select"
              name={makeFieldName(base, index, 'params.position')}
              label="Position"
              options={Object.values(RelativePosition)}
              defaultValue={defaultValueRelativeParams.position}
            />
          )}
          {mode === ValueMode.REACTION && (
            <>
              <TradingFormField
                type="select"
                name={makeFieldName(base, index, 'params.scope')}
                label="Scope"
                options={Object.values(ValueReactionScope)}
                defaultValue={defaultValueReactionParams.scope}
              />
              <TradingFormField
                type="select"
                name={makeFieldName(base, index, 'params.direction')}
                label="Direction"
                options={Object.values(ValueReactionDirection)}
                defaultValue={defaultValueReactionParams.direction}
              />
              <TradingFormField
                type="select"
                name={makeFieldName(base, index, 'params.event')}
                label="Event"
                options={Object.values(ValueReactionEvent)}
                defaultValue={defaultValueReactionParams.event}
              />
              {event === ValueReactionEvent.RETEST && (
                <TradingFormField
                  type="numeric"
                  name={makeFieldName(base, index, 'params.touchTolerancePct')}
                  label="Touch tolerance %"
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                />
              )}
              {event === ValueReactionEvent.BREAK && (
                <>
                  <TradingFormField
                    type="numeric"
                    name={makeFieldName(base, index, 'params.preBreakCandles')}
                    label="Pre-break candles"
                    inputProps={{ min: 1, step: 1 }}
                  />
                  <TradingFormField
                    type="numeric"
                    name={makeFieldName(
                      base,
                      index,
                      'params.breakMinDistancePct'
                    )}
                    label="Break min distance %"
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                  />
                </>
              )}
              <TradingFormField
                type="checkbox"
                name={makeFieldName(base, index, 'params.keepSignalState')}
                label="Keep signal state"
                checked={Boolean(reactionParams.keepSignalState)}
              />
              <TradingFormField
                type="numeric"
                name={makeFieldName(base, index, 'params.confirmationCandles')}
                label="Confirmation candles"
                inputProps={{ min: 0, step: 1 }}
              />
              {scope === ValueReactionScope.HISTORICAL && (
                <TradingFormField
                  type="numeric"
                  name={makeFieldName(
                    base,
                    index,
                    'params.maxHistoricalLevels'
                  )}
                  label="Max historical levels"
                  inputProps={{ min: 1, step: 1 }}
                />
              )}
              <TradingFormField
                type="numeric"
                name={makeFieldName(base, index, 'params.cooldownBarsPerLevel')}
                label="Cooldown bars per level"
                inputProps={{ min: 0, step: 1 }}
              />
            </>
          )}
        </>
      );
    }
    case SignalType.LIQUIDATION: {
      const liquidationParams =
        fieldValue.params ?? defaultLiquidationParams;

      return (
        <>
          <TradingFormField
            type="select"
            name={makeFieldName(base, index, 'params.mode')}
            label="Mode"
            options={Object.values(LiquidationSignalMode)}
            defaultValue={defaultLiquidationParams.mode}
          />
          <TradingFormField
            type="checkbox"
            name={makeFieldName(base, index, 'params.keepSignalState')}
            label="Keep signal state"
            checked={Boolean(liquidationParams.keepSignalState)}
          />
        </>
      );
    }
    default:
      return null;
  }
};

type StrategyConfigFormProps = {
  strategyPath: StrategyPath;
  timeframeIndex: number;
  enableSignalBlockTemplates?: boolean;
  onManageSignalBlockTemplates?: (payload: {
    timeframeIndex: number;
    strategyTarget: SignalBlockTarget;
    activeTemplateIds: string[];
  }) => void;
};

export const StrategyConfigForm: React.FC<StrategyConfigFormProps> = ({
  strategyPath,
  timeframeIndex,
  enableSignalBlockTemplates = true,
  onManageSignalBlockTemplates
}) => {
  const { watch } = useTradingFormContext();
  const { setUiState, getUiState } = useAdditionalTradingFormContext();
  const plotEntryTriggersFieldName =
    `${strategyPath}.visualization.plotEntryTriggers` as `timeframes.${number}.strategy.visualization.plotEntryTriggers`;
  const plotEntryConfluenceTriggersFieldName =
    `${strategyPath}.visualization.plotEntryConfluenceTriggers` as `timeframes.${number}.strategy.visualization.plotEntryConfluenceTriggers`;
  // Marker-plot flags live in form state (not uiState) so templates/layouts can
  // persist them without extra scope-key mapping logic.
  const plotEntryTriggers = Boolean(watch(plotEntryTriggersFieldName));
  const plotEntryConfluenceTriggers = Boolean(
    watch(plotEntryConfluenceTriggersFieldName)
  );

  // Load templates once
  useTemplates({ type: 'strategy', path: strategyPath, timeframeIndex });
  // Signal blocks are strategy-slot templates (entries/exits) composed into
  // current strategy form. They share the same strategy path reducer.
  useTemplates({
    type: 'signalBlock',
    path: strategyPath,
    timeframeIndex,
    enabled: enableSignalBlockTemplates
  });

  // Helper to create paths dynamically
  const makePath = (
    direction: 'long' | 'short',
    ...extra: UiStatePath[number][]
  ): UiStatePath =>
    [
      'sections',
      'timeframes',
      'items',
      timeframeIndex,
      'strategy',
      direction,
      ...extra
    ] as UiStatePath;

  const renderEntryExitSection = (
    direction: 'long' | 'short',
    type: StrategyBucket
  ) => {
    const sectionPath = makePath(direction, type);
    const expanded = getUiState(sectionPath, { expanded: true }).expanded;
    const configuredSignalsPath = makePath(direction, type, 'details');
    const configuredSignalsExpanded = getUiState(configuredSignalsPath, {
      expanded: true
    }).expanded;
    const signalBlockTarget = `${direction}.${type}` as SignalBlockTarget;

    return (
      <Section
        title={type.charAt(0).toUpperCase() + type.slice(1)}
        sx={sectionPanelStyle}
        actionButtons={
          enableSignalBlockTemplates ? (
            <FormConfigButtons
              templateType="signalBlock"
              timeframeIndex={timeframeIndex}
              strategyTarget={signalBlockTarget}
              onManageTemplates={
                onManageSignalBlockTemplates
                  ? () =>
                      onManageSignalBlockTemplates({
                        timeframeIndex,
                        strategyTarget: signalBlockTarget,
                        activeTemplateIds: []
                      })
                  : undefined
              }
              showSave={false}
            />
          ) : undefined
        }
        collapsible
        expanded={expanded}
        onToggle={(expanded) => {
          const current = getUiState<Record<string, unknown>>(sectionPath, {});
          setUiState(sectionPath, { ...current, expanded });
        }}
      >
        <Section
          title="Configured signals"
          sx={{
            ...sectionHeaderPanelStyle,
            backgroundColor: 'transparent',
            backgroundImage: 'none',
            boxShadow: 'none',
            border: '1px solid',
            borderColor: colorTokens.whiteAlpha22
          }}
          collapsible
          expanded={configuredSignalsExpanded}
          onToggle={(expanded) => {
            const current = getUiState<Record<string, unknown>>(
              configuredSignalsPath,
              {}
            );
            setUiState(configuredSignalsPath, { ...current, expanded });
          }}
        >
          <SignalConditionsForm
            strategyPath={strategyPath}
            direction={direction}
            type={type}
          />
        </Section>
      </Section>
    );
  };

  const renderStrategySection = (direction: 'long' | 'short') => {
    const sectionPath = makePath(direction);
    const expanded = getUiState(sectionPath, { expanded: true }).expanded;

    return (
      <Section
        key={direction}
        title={direction.charAt(0).toUpperCase() + direction.slice(1)}
        sx={sectionHeaderPanelStyle}
        collapsible
        expanded={expanded}
        onToggle={(expanded) => {
          const current = getUiState<Record<string, unknown>>(sectionPath, {});
          setUiState(sectionPath, { ...current, expanded });
        }}
      >
        {renderEntryExitSection(direction, 'entries')}
        {renderEntryExitSection(direction, 'exits')}
      </Section>
    );
  };

  return (
    <>
      <Section sx={sectionPanelStyle}>
        <FormContainer>
          <TradingFormField
            type="checkbox"
            name={plotEntryTriggersFieldName as TradingFormFieldName}
            label="Plot TF triggers"
            checked={plotEntryTriggers}
          />
          <TradingFormField
            type="checkbox"
            name={plotEntryConfluenceTriggersFieldName as TradingFormFieldName}
            label="Plot MTF confluence"
            checked={plotEntryConfluenceTriggers}
          />
        </FormContainer>
      </Section>
      {(['long', 'short'] as const).map((direction) =>
        renderStrategySection(direction)
      )}
    </>
  );
};

export type SignalConditionsFormProps = {
  strategyPath: StrategyPath;
  type: StrategyBucket;
  direction: 'long' | 'short';
};

export const SignalConditionsForm: React.FC<SignalConditionsFormProps> = ({
  strategyPath,
  type,
  direction
}) => {
  const { control } = useTradingFormContext();
  const { watch } = useFormContext();

  const fieldName = `${strategyPath}.value.${direction}.${type}` as const;

  const { fields, append, remove, update, replace } = useFieldArray({
    control,
    name: fieldName
  });

  const watchedFieldValues = watch(fieldName) as SignalCondition[] | undefined;
  const fieldValues = watchedFieldValues ?? emptySignalConditions;

  // Populate fields on initial load to keep in sync with the form state
  useEffect(() => {
    // Only run if the structure (length) changes — avoid infinite loops due to unstable refs
    if (fieldValues.length !== fields.length) {
      replace(fieldValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValues.length, fields.length, replace]);

  useEffect(() => {
    // Keep form state canonical: if strategy rows miss params required by mode
    // (for VALUE/MA), patch them once so field components read complete data.
    fieldValues.forEach((condition, index) => {
      const normalizedCondition = normalizeSignalCondition(condition);
      if (!isEqual(condition, normalizedCondition)) {
        update(index, normalizedCondition);
      }
    });
  }, [fieldValues, update]);

  return (
    <>
      {fields.map((field, index) => {
        const base = fieldName; // e.g. "timeframes.0.strategy.value.long.entries"

        const fieldValue = fieldValues[index];
        const isSignalEnabled = fieldValue?.enabled !== false;
        const isPlotEnabled = Boolean(fieldValue?.plot);

        return (
          <Box key={field.id} sx={{ position: 'relative' }}>
            <Section
              sx={{
                ...sectionHeaderPanelStyle,
                opacity: isSignalEnabled ? 1 : 0.62,
                borderColor: isSignalEnabled
                  ? colorTokens.whiteAlpha06
                  : colorTokens.whiteAlpha22,
                backgroundColor: isSignalEnabled
                  ? 'transparent'
                  : colorTokens.whiteAlpha02
              }}
            >
              <FormArrayItem index={index} onRemove={remove}>
                <Box sx={{ flex: 1 }}>
                  {!isSignalEnabled && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'inline-flex',
                        mb: 1,
                        px: 0.8,
                        py: 0.2,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: colorTokens.whiteAlpha22,
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5
                      }}
                    >
                      Disabled
                    </Typography>
                  )}
                  <FormContainer>
                    <TradingFormField
                      type="checkbox"
                      name={makeFieldName(base, index, 'enabled')}
                      label="Enabled"
                      checked={isSignalEnabled}
                    />

                    <TradingFormField
                      type="select"
                      name={makeFieldName(base, index, 'operator')}
                      label="Operator"
                      options={['AND', 'OR', 'NOT']}
                      defaultValue="AND"
                    />

                    <TradingFormField
                      type="autocomplete"
                      name={makeFieldName(base, index, 'value')}
                      label="Select signal"
                      options={strategySignalOptions}
                      defaultValue={fieldValue?.value}
                      onChange={(_, value) => {
                        if (!isSignalType(value)) return;
                        // useFieldArray.update() remounts the row. Deferring to the next frame
                        // lets MUI close the autocomplete popup before the remount happens.
                        requestAnimationFrame(() => {
                          update(
                            index,
                            buildUpdatedSignalCondition(fieldValue, value)
                          );
                        });
                      }}
                    />

                    <TradingFormField
                      type="checkbox"
                      name={makeFieldName(base, index, 'plot')}
                      label="Plot"
                      checked={isPlotEnabled}
                    />

                    {renderSignalParameterFields(
                      base,
                      index,
                      fieldValue,
                      (mode) => {
                        update(
                          index,
                          buildUpdatedValueSignalCondition(fieldValue, mode)
                        );
                      }
                    )}
                  </FormContainer>
                </Box>
              </FormArrayItem>
            </Section>
          </Box>
        );
      })}

      <AppIconButton
        tone="primary"
        title="Add Signal"
        aria-label="Add Signal"
        onClick={() => append({} as SignalCondition)}
      >
        <AddIcon />
      </AppIconButton>
    </>
  );
};
